import { PromptAssertionType, PromptTestCaseOrigin, PromptType, PromptVariableType } from '@today/types'
import { z } from 'zod'

// ─── Shared value objects ─────────────────────────────────────────────────────
//
// The nested shapes that ride inside a prompt body/case. Field names follow the
// domain interfaces in `@today/types` (camelCase), which the HTTP Contract in the
// spec names verbatim (`{ code, name, type, body, variables[], modelConfig?,
// outputSchema? }`). The owning tenant is ALWAYS resolved from the authenticated
// principal, never a body field — no schema below carries `tenantId`.

/** A declared `{{variable}}` — see {@link PromptVariable}. */
export const promptVariableSchema = z.object({
	key: z.string().min(1).max(128),
	type: z.enum(PromptVariableType),
	label: z.string().max(255).optional(),
	defaultValue: z.unknown().optional(),
	options: z.array(z.string()).optional(),
	listCode: z.string().max(128).optional(),
	itemTemplate: z.string().max(4096).optional(),
	joinKeys: z.array(z.string()).optional(),
	joinSeparator: z.string().optional(),
})

/** One reusable option in a variable list — see {@link PromptVariableListItem}. */
export const promptVariableListItemSchema = z.object({
	value: z.string().min(1),
	label: z.string().min(1),
})

/** Frozen, reporting-only model hints — see {@link PromptModelConfig}. */
export const promptModelConfigSchema = z.object({
	model: z.string().optional(),
	temperature: z.number().optional(),
	maxOutputTokens: z.number().int().optional(),
	effort: z.string().optional(),
})

/**
 * A single assertion on a test case — see {@link PromptAssertion}. `value` is bounded:
 * an `LLM_RUBRIC` assertion's `value` becomes the judge criterion the eval pipeline
 * sends to the model, so an unbounded value is a cost-amplification vector (it must not
 * exceed the judge input's `criterion` ceiling in the use-case registry).
 */
export const promptAssertionSchema = z.object({
	type: z.enum(PromptAssertionType),
	value: z.string().max(4_096).optional(),
	max: z.number().int().optional(),
})

// ─── Prompt head (create / update) ────────────────────────────────────────────

/**
 * Create body for `POST /prompts`. Server-assigned fields (`lifecycle = draft`,
 * `currentVersion = 0`) are NOT accepted. `code` is the immutable public handle,
 * unique per tenant forever (incl. soft-deleted). The undeclared-`{{placeholder}}`
 * check (REQ-001, `PROMPT_VARIABLE_UNDECLARED`) is semantic and enforced in the
 * service, not by this schema.
 */
export const promptCreateSchema = z.object({
	code: z.string().min(1).max(128),
	name: z.string().min(1).max(255),
	type: z.enum(PromptType),
	body: z.string().min(1).max(32_768),
	variables: z.array(promptVariableSchema).default([]),
	description: z.string().max(1024).optional(),
	modelConfig: promptModelConfigSchema.optional(),
	outputSchema: z.record(z.string(), z.unknown()).optional(),
})

/**
 * Update body for `PUT /prompts/{id}`. All fields optional. `code` is LISTED here
 * so a change attempt is detectable, but the immutability rule (REQ-001, 422
 * `PROMPT_CODE_IMMUTABLE`) is enforced in the service — the schema accepts it, the
 * service rejects any change.
 */
export const promptUpdateSchema = z
	.object({
		code: z.string().min(1).max(128),
		name: z.string().min(1).max(255),
		type: z.enum(PromptType),
		body: z.string().min(1).max(32_768),
		variables: z.array(promptVariableSchema),
		description: z.string().max(1024).nullable(),
		modelConfig: promptModelConfigSchema.nullable(),
		outputSchema: z.record(z.string(), z.unknown()).nullable(),
	})
	.partial()

// ─── Render ───────────────────────────────────────────────────────────────────

/**
 * Render body for `POST /prompts/{id}/render` (and the by-code variant). The
 * render target (head / version / label) is a query param; the body carries only
 * the `values` payload. Unknown keys / out-of-options values are rejected in the
 * pure render function (REQ-004), not here.
 */
export const promptRenderSchema = z.object({
	values: z.record(z.string(), z.unknown()),
})

// ─── Variable list (create / update) ──────────────────────────────────────────

/** Create body for `POST /prompt-lists`. `code` unique per tenant forever. */
export const promptVariableListCreateSchema = z.object({
	code: z.string().min(1).max(128),
	name: z.string().min(1).max(255),
	description: z.string().max(1024).optional(),
	items: z.array(promptVariableListItemSchema).default([]),
})

/** Update body for `PUT /prompt-lists/{id}`. `code` is immutable (service-enforced). */
export const promptVariableListUpdateSchema = z
	.object({
		name: z.string().min(1).max(255),
		description: z.string().max(1024).nullable(),
		items: z.array(promptVariableListItemSchema),
	})
	.partial()

// ─── Test case (create / update) ──────────────────────────────────────────────

/** Create body for `POST /prompts/{id}/test-cases`. */
export const promptTestCaseCreateSchema = z.object({
	name: z.string().min(1).max(255),
	values: z.record(z.string(), z.unknown()).default({}),
	assertions: z.array(promptAssertionSchema).default([]),
	origin: z.enum(PromptTestCaseOrigin).optional(),
})

/**
 * Update body for `PATCH /prompt-test-cases/{caseId}`. Editing a `generated` case
 * flips it to `manual` (service concern); the schema only validates shape.
 */
export const promptTestCaseUpdateSchema = z
	.object({
		name: z.string().min(1).max(255),
		values: z.record(z.string(), z.unknown()),
		assertions: z.array(promptAssertionSchema),
	})
	.partial()

// ─── Per-tenant defaults & settings ───────────────────────────────────────────

/**
 * Upsert body for `PUT /prompt-defaults/{promptType}`. The `promptType` axis is a
 * path param; the body carries the merged model-config for that type.
 */
export const promptDefaultConfigUpsertSchema = z.object({
	modelConfig: promptModelConfigSchema,
})

/** Update body for `PUT /prompt-settings` (the eval gate toggle). */
export const promptTenantSettingsUpdateSchema = z.object({
	requirePassingEvals: z.boolean(),
})

// ─── Labels ───────────────────────────────────────────────────────────────────

/**
 * Body for `PUT /prompts/{id}/labels/{label}`. Points the (path) label at a
 * `version`; `pinned` optionally holds a rollback (REQ-005). The unpublished-prompt
 * refusal (409 `PROMPT_NOT_PUBLISHED`) is a service concern.
 */
export const promptLabelMoveSchema = z.object({
	version: z.number().int().positive(),
	pinned: z.boolean().optional(),
})

// ─── Inferred input types ─────────────────────────────────────────────────────

export type PromptCreateInput = z.infer<typeof promptCreateSchema>
export type PromptUpdateInput = z.infer<typeof promptUpdateSchema>
export type PromptRenderInput = z.infer<typeof promptRenderSchema>
export type PromptVariableListCreateInput = z.infer<typeof promptVariableListCreateSchema>
export type PromptVariableListUpdateInput = z.infer<typeof promptVariableListUpdateSchema>
export type PromptTestCaseCreateInput = z.infer<typeof promptTestCaseCreateSchema>
export type PromptTestCaseUpdateInput = z.infer<typeof promptTestCaseUpdateSchema>
export type PromptDefaultConfigUpsertInput = z.infer<typeof promptDefaultConfigUpsertSchema>
export type PromptTenantSettingsUpdateInput = z.infer<typeof promptTenantSettingsUpdateSchema>
export type PromptLabelMoveInput = z.infer<typeof promptLabelMoveSchema>
